import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Globe, MessageCircleMore, Type, BarChart3, Save, MessageSquare, XCircle } from 'lucide-react';
import FieldHelp from '@/components/help/FieldHelp';
import { VERTICALS, DEFAULT_SHARED_CONTENT, flattenVerticalDefaults } from '../landingVerticals';

// Sprint 73.2 — un modo mailto/form independiente por cada punto de
// contacto de la landing pública (ya no un único interruptor compartido).
const CTA_TOUCHPOINTS = [
  { key: 'cta_mode_nav', label: '"Solicitar demo" (barra de navegación)' },
  { key: 'cta_mode_hero', label: 'Botón principal del hero' },
  { key: 'cta_mode_final', label: 'Botón del CTA final' },
  { key: 'cta_mode_footer_contact', label: 'Enlace "Contacto" del pie de página' },
];

const VERTICAL_TEXT_FIELDS = [
  { key: 'label', label: 'Nombre de la industria (público)' },
  { key: 'shortLabel', label: 'Nombre corto (usado dentro de frases)' },
  { key: 'eyebrow', label: 'Eyebrow del hero (encabezado pequeño arriba del título)' },
  { key: 'problem', label: 'Problema (hero)', area: true },
  { key: 'agitation', label: 'Agitación del problema (hero)', area: true },
  { key: 'solution', label: 'Solución que ofrece CavernicolApp (hero)', area: true },
  { key: 'job', label: 'Trabajo a resolver (franja "El trabajo real")', area: true },
  { key: 'outcome', label: 'Resultado esperado (franja "El trabajo real")', area: true },
  { key: 'alert', label: 'Alerta simulada en la demo visual' },
  { key: 'resource', label: 'Recurso simulado en la demo visual' },
  { key: 'lead', label: 'Lead simulado en la demo visual' },
  { key: 'nextAction', label: 'Siguiente acción simulada en la demo visual' },
  { key: 'proofPrompt', label: 'Frase de prueba social ("sólo verás evidencia de...")' },
  { key: 'benefit1_title', label: 'Beneficio 1 — título' },
  { key: 'benefit1_desc', label: 'Beneficio 1 — descripción', area: true },
  { key: 'benefit2_title', label: 'Beneficio 2 — título' },
  { key: 'benefit2_desc', label: 'Beneficio 2 — descripción', area: true },
  { key: 'benefit3_title', label: 'Beneficio 3 — título' },
  { key: 'benefit3_desc', label: 'Beneficio 3 — descripción', area: true },
  { key: 'proofMetric1', label: 'Métrica de prueba 1' },
  { key: 'proofMetric2', label: 'Métrica de prueba 2' },
  { key: 'proofMetric3', label: 'Métrica de prueba 3' },
];

const SHARED_TEXT_FIELDS = [
  { key: 'hero_badge', label: 'Hero — badge superior' },
  { key: 'hero_title_static', label: 'Hero — título (parte fija)' },
  { key: 'hero_title_emphasis', label: 'Hero — título (parte en color)' },
  { key: 'hero_trust_1', label: 'Hero — chip de confianza 1' },
  { key: 'hero_trust_2', label: 'Hero — chip de confianza 2' },
  { key: 'hero_trust_3', label: 'Hero — chip de confianza 3' },
  { key: 'beneficios_kicker', label: 'Beneficios — kicker' },
  { key: 'beneficios_heading_static', label: 'Beneficios — título (parte fija)' },
  { key: 'beneficios_heading_emphasis', label: 'Beneficios — título (parte en color). Usa {{vertical}} para insertar el nombre de la industria seleccionada.' },
  { key: 'producto_kicker', label: 'Producto — kicker' },
  { key: 'producto_heading_static', label: 'Producto — título (parte fija)' },
  { key: 'producto_heading_emphasis', label: 'Producto — título (parte en color)' },
  { key: 'producto_copy', label: 'Producto — párrafo', area: true },
  { key: 'evidencia_kicker', label: 'Evidencia — kicker' },
  { key: 'how_kicker', label: 'Cómo funciona — kicker' },
  { key: 'how_heading_static', label: 'Cómo funciona — título (parte fija)' },
  { key: 'how_heading_emphasis', label: 'Cómo funciona — título (parte en color)' },
  { key: 'how_step1_title', label: 'Paso 1 — título' },
  { key: 'how_step1_desc', label: 'Paso 1 — descripción', area: true },
  { key: 'how_step2_title', label: 'Paso 2 — título' },
  { key: 'how_step2_desc', label: 'Paso 2 — descripción', area: true },
  { key: 'how_step3_title', label: 'Paso 3 — título' },
  { key: 'how_step3_desc', label: 'Paso 3 — descripción', area: true },
  { key: 'final_heading_static', label: 'CTA final — título (parte fija)' },
  { key: 'final_heading_emphasis', label: 'CTA final — título (parte en color)' },
  { key: 'capability_pills_csv', label: 'Pills de capacidades (separadas por coma)', area: true },
];

const EMPTY_CONFIG = {
  cta_mode_nav: 'mailto', cta_mode_hero: 'mailto', cta_mode_final: 'mailto', cta_mode_footer_contact: 'mailto',
  contact_email: '', notify_mode: 'none', webhook_url: '', tenant_id: '',
  ga4_measurement_id: '', gtm_container_id: '', meta_pixel_id: '',
  meta_capi_access_token: '', meta_capi_test_event_code: '',
  linkedin_partner_id: '', tiktok_pixel_id: '',
};

function TextField({ field, value, placeholder, onChange }) {
  const Comp = field.area ? Textarea : Input;
  return (
    <FieldHelp label={field.label} htmlFor={`lc-${field.key}`}>
      <Comp
        id={`lc-${field.key}`}
        value={value || ''}
        placeholder={placeholder || ''}
        onChange={(e) => onChange(field.key, e.target.value)}
        rows={field.area ? 3 : undefined}
        data-testid={`landing-content-field-${field.key}`}
      />
    </FieldHelp>
  );
}

export default function LandingConfig() {
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  const [shared, setShared] = useState({});
  const [verticalsContent, setVerticalsContent] = useState({});
  const [activeVertical, setActiveVertical] = useState('beauty');
  const [savingContent, setSavingContent] = useState(false);

  // Sprint 73.2 — WhatsApp es un modo de contacto independiente, respaldado
  // por una campaña real (LeadSource) sobre el número YA conectado del
  // tenant seleccionado arriba, no un número de texto libre.
  const [waInstances, setWaInstances] = useState([]);
  const [waCampaign, setWaCampaign] = useState(null);
  const [waInstanceId, setWaInstanceId] = useState('');
  const [waTriggerMessage, setWaTriggerMessage] = useState('');
  const [loadingWa, setLoadingWa] = useState(false);
  const [savingWa, setSavingWa] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, contentRes, tenantsRes] = await Promise.allSettled([
        api.get('/admin/landing/config'),
        api.get('/admin/landing/content'),
        api.get('/admin/tenants', { params: { limit: 100 } }),
      ]);
      if (cfgRes.status === 'fulfilled') {
        setConfig({ ...EMPTY_CONFIG, ...cfgRes.value.data, meta_capi_access_token: '' });
      }
      if (contentRes.status === 'fulfilled') {
        setShared(contentRes.value.data.shared || {});
        setVerticalsContent(contentRes.value.data.verticals || {});
      }
      if (tenantsRes.status === 'fulfilled') {
        setTenants(tenantsRes.value.data || []);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadWhatsapp = useCallback(async (tenantId) => {
    if (!tenantId) {
      setWaInstances([]); setWaCampaign(null); setWaInstanceId(''); setWaTriggerMessage('');
      return;
    }
    setLoadingWa(true);
    try {
      const [instancesRes, campaignRes] = await Promise.allSettled([
        api.get('/admin/landing/whatsapp/tenant-instances', { params: { tenant_id: tenantId } }),
        api.get('/admin/landing/whatsapp/campaign', { params: { tenant_id: tenantId } }),
      ]);
      const instances = instancesRes.status === 'fulfilled' ? (instancesRes.value.data || []) : [];
      setWaInstances(instances);
      const campaign = campaignRes.status === 'fulfilled' && campaignRes.value.data?.lead_source_id
        ? campaignRes.value.data : null;
      setWaCampaign(campaign);
      setWaInstanceId(campaign?.instance_id || (instances[0]?.id || ''));
      setWaTriggerMessage(campaign?.trigger_message || '');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingWa(false);
    }
  }, []);

  useEffect(() => { if (config.tenant_id) loadWhatsapp(config.tenant_id); }, [config.tenant_id, loadWhatsapp]);

  const handleSaveWhatsapp = async () => {
    setSavingWa(true);
    try {
      const { data } = await api.put('/admin/landing/whatsapp/campaign', {
        tenant_id: config.tenant_id, instance_id: waInstanceId, trigger_message: waTriggerMessage,
      });
      setWaCampaign(data);
      toast.success('Campaña de WhatsApp de la Landing guardada.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingWa(false);
    }
  };

  const handleClearWhatsapp = async () => {
    setSavingWa(true);
    try {
      await api.delete('/admin/landing/whatsapp/campaign', { params: { tenant_id: config.tenant_id } });
      setWaCampaign(null);
      setWaTriggerMessage('');
      toast.success('Burbuja de WhatsApp desactivada.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingWa(false);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const { data } = await api.put('/admin/landing/config', config);
      setConfig({ ...EMPTY_CONFIG, ...data, meta_capi_access_token: '' });
      toast.success('Configuración de Landing guardada.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveContent = async () => {
    setSavingContent(true);
    try {
      const { data } = await api.put('/admin/landing/content', { shared, verticals: verticalsContent });
      setShared(data.shared || {});
      setVerticalsContent(data.verticals || {});
      toast.success('Textos de la Landing guardados.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingContent(false);
    }
  };

  const setSharedField = (key, value) => setShared((prev) => ({ ...prev, [key]: value }));
  const setVerticalField = (key, value) => setVerticalsContent((prev) => ({
    ...prev, [activeVertical]: { ...(prev[activeVertical] || {}), [key]: value },
  }));

  // Sprint 73.2 — cada campo vacío muestra el valor vigente como marca de
  // agua, para que el admin ubique visualmente qué sección/vertical edita.
  const activeVerticalDefaults = flattenVerticalDefaults(VERTICALS.find((v) => v.id === activeVertical) || VERTICALS[0]);

  if (loading) {
    return <div className="p-8 text-sm text-[#64748B]">Cargando configuración de Landing…</div>;
  }

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2"><Globe className="w-6 h-6" /> Landing</h1>
        <p className="text-sm text-[#64748B] mt-1">
          Configura el formulario de contacto público, la burbuja de WhatsApp, los textos de cada
          sección de la landing (adaptados por industria) y los pixeles/CAPI de analítica y anuncios.
        </p>
      </div>

      <Tabs defaultValue="contact" className="space-y-4">
        <TabsList className="bg-white border border-[#E2E8F0] rounded-lg p-1 flex flex-wrap">
          <TabsTrigger value="contact" data-testid="landing-tab-contact">
            <MessageCircleMore className="w-4 h-4 mr-1" /> Formulario y contacto
          </TabsTrigger>
          <TabsTrigger value="content" data-testid="landing-tab-content">
            <Type className="w-4 h-4 mr-1" /> Copys por vertical
          </TabsTrigger>
          <TabsTrigger value="pixels" data-testid="landing-tab-pixels">
            <BarChart3 className="w-4 h-4 mr-1" /> Pixeles y analítica
          </TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Formulario y contacto ─────────────────────────────── */}
        <TabsContent value="contact" className="space-y-5 bg-white border border-[#E2E8F0] rounded-lg p-5">
          <div>
            <h2 className="font-semibold text-[#0F172A] mb-1">Botones de contacto</h2>
            <p className="text-xs text-[#64748B] mb-3">
              Cada punto de contacto de la landing decide, por su cuenta, si abre el correo del visitante
              o lo lleva al formulario en la misma página.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {CTA_TOUCHPOINTS.map((tp) => (
                <FieldHelp key={tp.key} label={tp.label} help="Independiente de los demás puntos de contacto.">
                  <Select value={config[tp.key]} onValueChange={(v) => setConfig((p) => ({ ...p, [tp.key]: v }))}>
                    <SelectTrigger data-testid={`landing-${tp.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mailto">Abrir el correo del visitante (mailto)</SelectItem>
                      <SelectItem value="form">Llevar al formulario de contacto de la página</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldHelp>
              ))}
            </div>
          </div>

          <FieldHelp label="Correo de contacto" help="Usado para el modo mailto y, si activas notificación por correo, para avisos de nuevos contactos.">
            <Input value={config.contact_email} onChange={(e) => setConfig((p) => ({ ...p, contact_email: e.target.value }))} placeholder="contacto@tuempresa.com" data-testid="landing-contact-email" />
          </FieldHelp>

          <FieldHelp label="Tenant donde se crean los leads del formulario" help="Cada envío del formulario crea un contacto y una oportunidad real en el CRM de este tenant, además de disparar sus automatizaciones y reglas de asignación. Este mismo tenant es el que recibe los leads de la burbuja de WhatsApp de abajo.">
            <Select value={config.tenant_id} onValueChange={(v) => setConfig((p) => ({ ...p, tenant_id: v }))}>
              <SelectTrigger data-testid="landing-tenant-select"><SelectValue placeholder="Selecciona un tenant" /></SelectTrigger>
              <SelectContent>
                {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldHelp>

          <FieldHelp label="Notificación adicional al recibir un contacto" help="Independiente de la creación del lead en CRM — puedes avisar por correo, por webhook (Zapier/Make/etc.), ambos, o ninguno.">
            <Select value={config.notify_mode} onValueChange={(v) => setConfig((p) => ({ ...p, notify_mode: v }))}>
              <SelectTrigger data-testid="landing-notify-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ninguna (solo el lead en CRM)</SelectItem>
                <SelectItem value="email">Correo</SelectItem>
                <SelectItem value="webhook">Webhook</SelectItem>
                <SelectItem value="both">Correo y webhook</SelectItem>
              </SelectContent>
            </Select>
          </FieldHelp>

          <FieldHelp label="URL del webhook" help="Se le envía un POST en JSON con nombre, correo, teléfono, mensaje y UTMs cada vez que alguien envía el formulario. Déjalo vacío para conservar el valor ya guardado.">
            <Input value={config.webhook_url} onChange={(e) => setConfig((p) => ({ ...p, webhook_url: e.target.value }))} placeholder="https://hooks.zapier.com/..." data-testid="landing-webhook-url" />
          </FieldHelp>

          <p className="text-xs text-[#94A3B8]">
            El captcha del formulario reutiliza las mismas claves de Cloudflare Turnstile del login —
            configúralas en Super Admin → Infraestructura → Cloudflare Turnstile.
          </p>

          <Button onClick={handleSaveConfig} disabled={savingConfig} data-testid="landing-save-config">
            <Save className="w-4 h-4 mr-1" /> {savingConfig ? 'Guardando…' : 'Guardar formulario y contacto'}
          </Button>

          {/* ── WhatsApp — modo de contacto independiente, campaña real ─── */}
          <div className="border-t border-[#E2E8F0] pt-5">
            <h2 className="font-semibold text-[#0F172A] mb-1 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Burbuja de WhatsApp
            </h2>
            <p className="text-xs text-[#64748B] mb-3">
              Es un modo de contacto independiente de los botones de arriba. Apunta siempre a un número
              de WhatsApp YA CONECTADO del tenant seleccionado, y crea una campaña real de recepción de
              leads: cuando alguien escriba el mensaje configurado, se crea automáticamente un contacto y
              una oportunidad en el CRM de ese tenant.
            </p>

            {!config.tenant_id && (
              <p className="text-sm text-[#94A3B8]">Selecciona primero un tenant arriba.</p>
            )}

            {config.tenant_id && loadingWa && (
              <p className="text-sm text-[#94A3B8]">Cargando números de WhatsApp conectados…</p>
            )}

            {config.tenant_id && !loadingWa && waInstances.length === 0 && (
              <p className="text-sm text-[#94A3B8]">
                Este tenant no tiene ningún número de WhatsApp conectado todavía. Conéctalo primero desde
                su propio panel de Integraciones y vuelve a esta pantalla.
              </p>
            )}

            {config.tenant_id && !loadingWa && waInstances.length > 0 && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <FieldHelp label="Número de WhatsApp conectado" help="Solo se listan números realmente conectados de este tenant — no se puede escribir uno libremente, porque solo los mensajes que lleguen a un número conectado activan la campaña.">
                    <Select value={waInstanceId} onValueChange={setWaInstanceId}>
                      <SelectTrigger data-testid="landing-wa-instance"><SelectValue placeholder="Selecciona un número" /></SelectTrigger>
                      <SelectContent>
                        {waInstances.map((i) => (
                          <SelectItem key={i.id} value={i.id}>
                            {(i.instance_name ? `${i.instance_name} — ` : '') + (i.phone_number_masked || i.channel_identity || i.id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldHelp>
                  <FieldHelp label="Mensaje-gatillo" help="Cuando un visitante envíe exactamente este texto (o lo incluya) al número de arriba, se crea el lead automáticamente. También es el mensaje precargado del botón de la burbuja.">
                    <Input
                      value={waTriggerMessage}
                      onChange={(e) => setWaTriggerMessage(e.target.value)}
                      placeholder="Hola, quiero conocer más sobre CavernicolApp"
                      data-testid="landing-wa-trigger-message"
                    />
                  </FieldHelp>
                </div>

                {waCampaign && (
                  <p className="text-xs text-[#16A34A]">
                    Campaña activa — enlace de la burbuja: <span className="font-mono">{waCampaign.wa_me_link}</span>
                  </p>
                )}

                <div className="flex gap-2">
                  <Button onClick={handleSaveWhatsapp} disabled={savingWa || !waInstanceId || !waTriggerMessage.trim()} data-testid="landing-save-wa">
                    <Save className="w-4 h-4 mr-1" /> {savingWa ? 'Guardando…' : (waCampaign ? 'Actualizar campaña' : 'Activar burbuja de WhatsApp')}
                  </Button>
                  {waCampaign && (
                    <Button variant="outline" onClick={handleClearWhatsapp} disabled={savingWa} data-testid="landing-clear-wa">
                      <XCircle className="w-4 h-4 mr-1" /> Desactivar burbuja
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Tab 2: Copys por vertical ─────────────────────────────────── */}
        <TabsContent value="content" className="space-y-5 bg-white border border-[#E2E8F0] rounded-lg p-5">
          <div>
            <h2 className="font-semibold text-[#0F172A] mb-3">Textos compartidos (iguales para todas las industrias)</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {SHARED_TEXT_FIELDS.map((f) => (
                <TextField key={f.key} field={f} value={shared[f.key]} placeholder={DEFAULT_SHARED_CONTENT[f.key]} onChange={setSharedField} />
              ))}
            </div>
          </div>

          <div className="border-t border-[#E2E8F0] pt-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-semibold text-[#0F172A]">Textos por industria</h2>
              <Select value={activeVertical} onValueChange={setActiveVertical}>
                <SelectTrigger className="w-64" data-testid="landing-vertical-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VERTICALS.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {VERTICAL_TEXT_FIELDS.map((f) => (
                <TextField
                  key={f.key} field={f}
                  value={(verticalsContent[activeVertical] || {})[f.key]}
                  placeholder={activeVerticalDefaults[f.key]}
                  onChange={setVerticalField}
                />
              ))}
            </div>
          </div>

          <Button onClick={handleSaveContent} disabled={savingContent} data-testid="landing-save-content">
            <Save className="w-4 h-4 mr-1" /> {savingContent ? 'Guardando…' : 'Guardar textos'}
          </Button>
        </TabsContent>

        {/* ── Tab 3: Pixeles y analítica ────────────────────────────────── */}
        <TabsContent value="pixels" className="space-y-5 bg-white border border-[#E2E8F0] rounded-lg p-5">
          <div className="grid md:grid-cols-2 gap-4">
            <FieldHelp label="Google Analytics 4 — Measurement ID" help="Formato G-XXXXXXXXXX, en GA4 → Administrar → Flujos de datos.">
              <Input value={config.ga4_measurement_id} onChange={(e) => setConfig((p) => ({ ...p, ga4_measurement_id: e.target.value }))} placeholder="G-XXXXXXXXXX" data-testid="landing-ga4" />
            </FieldHelp>
            <FieldHelp label="Google Tag Manager — Container ID" help="Formato GTM-XXXXXXX. Si lo configuras, se usa en lugar de una etiqueta directa de GA4.">
              <Input value={config.gtm_container_id} onChange={(e) => setConfig((p) => ({ ...p, gtm_container_id: e.target.value }))} placeholder="GTM-XXXXXXX" data-testid="landing-gtm" />
            </FieldHelp>
            <FieldHelp label="Meta Pixel — Pixel ID" help="El mismo Pixel ID se usa en el navegador (Meta Pixel) y en el servidor (Conversions API) para el mismo evento — así Meta reconoce ambos como la misma señal.">
              <Input value={config.meta_pixel_id} onChange={(e) => setConfig((p) => ({ ...p, meta_pixel_id: e.target.value }))} data-testid="landing-meta-pixel" />
            </FieldHelp>
            <FieldHelp label="Meta CAPI — Access Token" help="Token de Conversions API (Events Manager → Configuración → Conversions API). Nunca se muestra de vuelta — déjalo vacío para conservar el valor guardado.">
              <Input type="password" value={config.meta_capi_access_token} onChange={(e) => setConfig((p) => ({ ...p, meta_capi_access_token: e.target.value }))} placeholder={config.has_meta_capi_token ? config.meta_capi_token_masked : ''} data-testid="landing-meta-capi-token" />
            </FieldHelp>
            <FieldHelp label="Meta CAPI — Test Event Code (opcional)" help="Solo para probar en el Test Events de Meta antes de ponerlo en producción. Quítalo cuando termines de probar.">
              <Input value={config.meta_capi_test_event_code} onChange={(e) => setConfig((p) => ({ ...p, meta_capi_test_event_code: e.target.value }))} data-testid="landing-meta-capi-test" />
            </FieldHelp>
            <FieldHelp label="LinkedIn Insight Tag — Partner ID" help="En LinkedIn Campaign Manager → Insight Tag.">
              <Input value={config.linkedin_partner_id} onChange={(e) => setConfig((p) => ({ ...p, linkedin_partner_id: e.target.value }))} data-testid="landing-linkedin-partner" />
            </FieldHelp>
            <FieldHelp label="TikTok Pixel — Pixel ID" help="En TikTok Ads Manager → Assets → Events.">
              <Input value={config.tiktok_pixel_id} onChange={(e) => setConfig((p) => ({ ...p, tiktok_pixel_id: e.target.value }))} data-testid="landing-tiktok-pixel" />
            </FieldHelp>
          </div>
          <p className="text-xs text-[#94A3B8]">
            Cuando llega tráfico de campañas (utm_source, utm_medium, utm_campaign, utm_content, utm_term
            en la URL), la landing los guarda automáticamente y los adjunta tanto al lead creado en CRM
            como a los eventos de Meta CAPI y al dataLayer de GTM/GA4 — no necesitas configurar nada
            adicional para que las UTMs viajen con el visitante.
          </p>
          <Button onClick={handleSaveConfig} disabled={savingConfig} data-testid="landing-save-pixels">
            <Save className="w-4 h-4 mr-1" /> {savingConfig ? 'Guardando…' : 'Guardar pixeles'}
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
